<template>
    <div class="enps-monthly-trend">
        <div class="enps-monthly-trend__title">Динамика средней оценки</div>

        <VSpinner v-if="isLoading"></VSpinner>
        <div class="enps-chart-placeholder" v-else-if="!hasChartData" style="height: 220px">Нет данных за период</div>
        <LineChart
            v-else
            :chart-data="chartData"
            :chart-options="chartOptions"
            :styles="{ height: '220px', position: 'relative' }"
        ></LineChart>
    </div>
</template>
<script>
// Components
import { Line } from 'vue-chartjs/legacy';
import VSpinner from '@/views/components/VProgress/VSpinner.vue';

// Other
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop, Watch } from 'vue-property-decorator';
import http from '@/js/http';
import ruMonthNames from '@/js/ruMonthNames';
import { SERIES } from '@/js/enpsColors';

const NORMAL_COLOR = SERIES.averageTrend.main;
const LOW_SAMPLE_COLOR = SERIES.averageTrend.lowSample;

// Линейный график, одна точка = один месяц, хронологический порядок слева направо (бэкенд уже
// строит trendData от старого месяца к текущему — monthlyAverages() в EnpsDashboardService.php
// идёт курсором вперёд, так что текущий месяц всегда последний элемент массива = крайняя правая
// точка без доп. сортировки на фронте). Горизонт — год, задан на бэкенде и НЕ связан с общим
// фильтром периода дашборда; переключатель 3/6/12 убран решением владельца продукта. Подсветка
// месяцев из фильтра периода удалена там же (2026-08-08): разноцветные кружки на линии
// читались как ошибка, а не как подсказка.
@Component({
    components: {
        // Не под родным именем Line: оно совпадает с SVG-элементом <line>, и Vue 2 отказывается
        // регистрировать компонент с зарезервированным именем — график просто не рендерится.
        LineChart: Line,
        VSpinner,
    },
})
class EnpsMonthlyTrendChart extends Vue {
    // Отдел, выбранный администратором на странице ('' — все): тренд обязан показывать тот же
    // срез, что и остальные виджеты, иначе цифры на одном экране противоречили бы друг другу.
    @Prop({ type: String, default: '' })
    department;

    trendData = [];
    isLoading = false;
    loadSeq = 0;

    // Не просто length > 0: бэкенд всегда отдаёт ровно N месяцев с нулевой заливкой, и линия
    // по одним null рисовалась бы пустым прямоугольником, который выглядит как сломанный
    // график, а не как «данных нет».
    get hasChartData() {
        return this.trendData.some((row) => row.ratingsCount > 0);
    }

    monthLabel(month) {
        const [year, monthNumber] = month.split('-').map(Number);

        return `${ruMonthNames[monthNumber - 1]} ${year}`;
    }

    pointColor(row) {
        return row.isLowSample ? LOW_SAMPLE_COLOR : NORMAL_COLOR;
    }

    get chartData() {
        return {
            labels: this.trendData.map((row) => [this.monthLabel(row.month), `(${row.ratingsCount} оценок)`]),
            datasets: [
                {
                    label: 'Средняя оценка',
                    // Без клипа по chartArea: точки на краях шкалы (0 и 5) иначе срезаются
                    // наполовину границей области графика.
                    clip: false,
                    // Месяц без оценок — 0, линия непрерывна и опускается к нулю (решение
                    // владельца продукта; альтернатива с null-разрывами отклонена). Подпись
                    // «(0 оценок)» под осью отличает такой месяц от настоящей нулевой средней.
                    data: this.trendData.map((row) => row.averageRating ?? 0),
                    borderColor: NORMAL_COLOR,
                    backgroundColor: NORMAL_COLOR,
                    pointBackgroundColor: this.trendData.map((row) => this.pointColor(row)),
                    pointBorderColor: this.trendData.map((row) => this.pointColor(row)),
                    pointRadius: 4,
                    tension: 0.3,
                },
            ],
        };
    }

    get chartOptions() {
        return {
            responsive: true,
            maintainAspectRatio: false,
            // Место под точки на краях шкалы (clip: false у датасета): легенда скрыта, и без
            // отступа верхней точке некуда рисоваться.
            layout: { padding: { top: 8 } },
            plugins: {
                legend: { display: false },
            },
            scales: {
                y: { min: 0, max: 5 },
            },
        };
    }

    @Watch('department')
    onDepartmentChange() {
        this.load();
    }

    load() {
        // Без клиентского кэша (убран решением владельца продукта): от гонок при быстром
        // переключении отдела защищает счётчик — применяется только последний запрос.
        const seq = ++this.loadSeq;

        this.isLoading = true;

        http.get('/enps/dashboard/trend', { params: { department: this.department } })
            .then(({ data }) => {
                if (seq !== this.loadSeq) {
                    return;
                }

                this.trendData = data;
            })
            .catch(() => {
                if (seq !== this.loadSeq) {
                    return;
                }

                this.trendData = [];
            })
            .finally(() => {
                if (seq === this.loadSeq) {
                    this.isLoading = false;
                }
            });
    }

    created() {
        this.load();
    }
}

export default EnpsMonthlyTrendChart;
</script>
<style lang="scss">
.enps-monthly-trend {
    flex: 1;
    padding: 15px;
    border-radius: 8px;
    background-color: var(--color-white);
    box-shadow: 0px 1px 10px rgba(88, 88, 88, 0.15);

    // Заголовок отбивается от графика тем же отступом, что раньше давала шапка с
    // переключателем горизонта, — высота виджета не должна поехать относительно соседнего
    // «Доля оценённых по месяцам», они стоят в одном ряду.
    &__title {
        margin-bottom: 10px;
        font-weight: 600;
    }
}
</style>
